import { styled } from '@mui/material/styles'
import { Box, ButtonBase, type ButtonBaseProps, type BoxProps } from '@mui/material'
import { youShallNotPass } from 'lib/helpers/youShallNotPass/youShallNotPass'
import * as tokens from '../../tokens/tokens'
import * as tkn from './FabBase.tokens'
import * as tkn_p from './FabPrimary.tokens'
import * as tkn_s from './FabSecondary.tokens'

export type RootProps = ButtonBaseProps<
  'button',
  {
    labelOn?: boolean
    /**
     * The kind to use.
     * @default 'neutral'
     */
    kind?: 'neutral' | 'destructive'
    /**
     * The variant to use.
     * @default 'primary'
     */
    variant?: 'primary' | 'secondary'
    /**
     * The size of the component
     * @default 'small'
     */
    size?: 'small' | 'medium'
    /**
     * Element placed before the children.
     */
    icon?: React.ReactNode
    /**
     * If `true`, the button is set to active state.
     * @default false
     */
    active?: boolean
    /**
     * If `true`, the button will have a drop shadow
     * @default false
     */
    shadow?: boolean
  }
>

export const getRootStyles = (
  {
    variant,
    kind,
    disabled,
    size,
    labelOn,
    active,
    shadow,
  }: Pick<RootProps, 'variant' | 'kind' | 'disabled' | 'size' | 'labelOn' | 'active' | 'shadow'>,
  { withHoverStyles = true } = {},
) => {
  const primaryNeutralActive = {
    backgroundColor: tkn_p.FabPrimaryColorPositivePressedBackground,
    color: tkn_p.FabPrimaryColorPositivePressedOnBackground,
    borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthPositivePressed,
    borderColor: tkn_p.FabPrimaryColorPositivePressedOutline,
  }

  const secondaryNeutralActive = {
    backgroundColor: tkn_s.FabSecondaryColorPositivePressedBackground,
    color: tkn_s.FabSecondaryColorPositivePressedOnBackground,
    borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthPositivePressed,
    borderColor: tkn_s.FabSecondaryColorPositivePressedOutline,
  }

  const primaryDestructiveActive = {
    backgroundColor: tkn_p.FabPrimaryColorDestructivePressedBackground,
    color: tkn_p.FabPrimaryColorDestructivePressedOnBackground,
    borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthDestructivePressed,
    borderColor: tkn_p.FabPrimaryColorDestructivePressedOutline,
  }

  const secondaryDestructiveActive = {
    backgroundColor: tkn_s.FabSecondaryColorDestructivePressedBackground,
    color: tkn_s.FabSecondaryColorDestructivePressedOnBackground,
    borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthDestructivePressed,
    borderColor: tkn_s.FabSecondaryColorDestructivePressedOutline,
  }

  return {
    textAlign: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    fontSize: tokens.ThemeTypographyDesktopButtonFontSize,
    lineHeight: tokens.ThemeTypographyDesktopButtonLineHeight,
    fontWeight: tokens.ThemeTypographyDesktopButtonFontWeight,
    letterSpacing: tokens.ThemeTypographyDesktopButtonLetterSpacing,
    fontFamily: tokens.ThemeTypographyDesktopButtonFontFamily,
    textIndent: tokens.ThemeTypographyDesktopButtonParagraphSpacing,
    ...(size === 'medium' && {
      height: tkn.FabBaseSizeDesktopMedium,
      borderRadius: tkn.FabBaseBorderRadiusDesktopMedium,
      ...(labelOn
        ? {
            paddingLeft: tkn.FabBaseSpacingLabelOnDesktopMediumLeft,
            paddingRight: tkn.FabBaseSpacingLabelOnDesktopMediumRight,
          }
        : {
            width: tkn.FabBaseSizeDesktopMedium,
            paddingLeft: tkn.FabBaseSpacingLabelOffDesktopMediumLeft,
            paddingRight: tkn.FabBaseSpacingLabelOffDesktopMediumRight,
          }),
    }),
    ...(size === 'small' && {
      height: tkn.FabBaseSizeDesktopSmall,
      borderRadius: tkn.FabBaseBorderRadiusDesktopSmall,
      ...(labelOn
        ? {
            paddingLeft: tkn.FabBaseSpacingLabelOnDesktopSmallLeft,
            paddingRight: tkn.FabBaseSpacingLabelOnDesktopSmallRight,
          }
        : {
            width: tkn.FabBaseSizeDesktopSmall,
            paddingLeft: tkn.FabBaseSpacingLabelOffDesktopSmallLeft,
            paddingRight: tkn.FabBaseSpacingLabelOffDesktopSmallRight,
          }),
    }),
    ...(variant === 'primary' &&
      kind === 'neutral' && {
        backgroundColor: tkn_p.FabPrimaryColorPositiveDefaultBackground,
        color: tkn_p.FabPrimaryColorPositiveDefaultOnBackground,
        borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthPositiveDefault,
        borderStyle: 'solid',
        borderColor: tkn_p.FabPrimaryColorPositiveDefaultOutline,
        ...(active && primaryNeutralActive),
        ...(!active &&
          withHoverStyles && {
            '&:active': primaryNeutralActive,
            '&:hover': {
              backgroundColor: tkn_p.FabPrimaryColorPositiveHoveredBackground,
              color: tkn_p.FabPrimaryColorPositiveHoveredOnBackground,
              borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthPositiveHovered,
              borderColor: tkn_p.FabPrimaryColorPositiveHoveredOutline,
            },
          }),
        '&:focus-visible': {
          backgroundColor: tkn_p.FabPrimaryColorPositiveHoveredBackground,
          color: tkn_p.FabPrimaryColorPositiveHoveredOnBackground,
          borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthPositiveHovered,
          borderColor: tkn_p.FabPrimaryColorPositiveHoveredOutline,
        },
        ...(disabled && {
          backgroundColor: tkn_p.FabPrimaryColorPositiveDisabledBackground,
          color: tkn_p.FabPrimaryColorPositiveDisabledOnBackground,
          borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthPositiveDisabled,
          borderColor: tkn_p.FabPrimaryColorPositiveDisabledOutline,
        }),
      }),
    ...(variant === 'secondary' &&
      kind === 'neutral' && {
        backgroundColor: tkn_s.FabSecondaryColorPositiveDefaultBackground,
        color: tkn_s.FabSecondaryColorPositiveDefaultOnBackground,
        borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthPositiveDefault,
        borderColor: tkn_s.FabSecondaryColorPositiveDefaultOutline,
        borderStyle: 'solid',
        ...(active && secondaryNeutralActive),
        ...(!active &&
          withHoverStyles && {
            '&:active': secondaryNeutralActive,
            '&:hover': {
              backgroundColor: tkn_s.FabSecondaryColorPositiveHoveredBackground,
              color: tkn_s.FabSecondaryColorPositiveHoveredOnBackground,
              borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthPositiveHovered,
              borderColor: tkn_s.FabSecondaryColorPositiveHoveredOutline,
            },
          }),
        '&:focus-visible': {
          backgroundColor: tkn_s.FabSecondaryColorPositiveHoveredBackground,
          color: tkn_s.FabSecondaryColorPositiveHoveredOnBackground,
          borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthPositiveHovered,
          borderColor: tkn_s.FabSecondaryColorPositiveHoveredOutline,
        },
        ...(disabled && {
          backgroundColor: tkn_s.FabSecondaryColorPositiveDisabledBackground,
          color: tkn_s.FabSecondaryColorPositiveDisabledOnBackground,
          borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthPositiveDisabled,
          borderColor: tkn_s.FabSecondaryColorPositiveDisabledOutline,
        }),
      }),
    ...(variant === 'primary' &&
      kind === 'destructive' && {
        backgroundColor: tkn_p.FabPrimaryColorDestructiveDefaultBackground,
        color: tkn_p.FabPrimaryColorDestructiveDefaultOnBackground,
        borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthDestructiveDefault,
        borderStyle: 'solid',
        borderColor: tkn_p.FabPrimaryColorDestructiveDefaultOutline,
        ...(active && primaryDestructiveActive),
        ...(!active &&
          withHoverStyles && {
            '&:active': primaryDestructiveActive,
            '&:hover': {
              backgroundColor: tkn_p.FabPrimaryColorDestructiveHoveredBackground,
              color: tkn_p.FabPrimaryColorDestructiveHoveredOnBackground,
              borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthDestructiveHovered,
              borderColor: tkn_p.FabPrimaryColorDestructiveHoveredOutline,
            },
          }),
        '&:focus-visible': {
          backgroundColor: tkn_p.FabPrimaryColorDestructiveHoveredBackground,
          color: tkn_p.FabPrimaryColorDestructiveHoveredOnBackground,
          borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthDestructiveHovered,
          borderColor: tkn_p.FabPrimaryColorDestructiveHoveredOutline,
        },
        ...(disabled && {
          backgroundColor: tkn_p.FabPrimaryColorDestructiveDisabledBackground,
          color: tkn_p.FabPrimaryColorDestructiveDisabledOnBackground,
          borderWidth: shadow ? 0 : tkn_p.FabPrimaryBorderWidthDestructiveDisabled,
          borderColor: tkn_p.FabPrimaryColorDestructiveDisabledOutline,
        }),
      }),
    ...(variant === 'secondary' &&
      kind === 'destructive' && {
        backgroundColor: tkn_s.FabSecondaryColorDestructiveDefaultBackground,
        color: tkn_s.FabSecondaryColorDestructiveDefaultOnBackground,
        borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthDestructiveDefault,
        borderStyle: 'solid',
        borderColor: tkn_s.FabSecondaryColorDestructiveDefaultOutline,
        ...(active && secondaryDestructiveActive),
        ...(!active &&
          withHoverStyles && {
            '&:active': secondaryDestructiveActive,
            '&:hover': {
              backgroundColor: tkn_s.FabSecondaryColorDestructiveHoveredBackground,
              color: tkn_s.FabSecondaryColorDestructiveHoveredOnBackground,
              borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthDestructiveHovered,
              borderColor: tkn_s.FabSecondaryColorDestructiveHoveredOutline,
            },
          }),
        '&:focus-visible': {
          backgroundColor: tkn_s.FabSecondaryColorDestructiveHoveredBackground,
          color: tkn_s.FabSecondaryColorDestructiveHoveredOnBackground,
          borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthDestructiveHovered,
          borderColor: tkn_s.FabSecondaryColorDestructiveHoveredOutline,
        },
        ...(disabled && {
          backgroundColor: tkn_s.FabSecondaryColorDestructiveDisabledBackground,
          color: tkn_s.FabSecondaryColorDestructiveDisabledOnBackground,
          borderWidth: shadow ? 0 : tkn_s.FabSecondaryBorderWidthDestructiveDisabled,
          borderColor: tkn_s.FabSecondaryColorDestructiveDisabledOutline,
        }),
      }),
    ...(shadow && {
      boxShadow: `${tokens.ThemeShadowDesktopLargeX} ${tokens.ThemeShadowDesktopLargeY} ${tokens.ThemeShadowDesktopLargeBlur} ${tokens.ThemeShadowDesktopLargeColor}`,
    }),
  } as const
}

export const Root = styled(ButtonBase, {
  shouldForwardProp: youShallNotPass('kind', 'variant', 'labelOn', 'active', 'shadow'),
})<RootProps>(getRootStyles)

type IconBox = BoxProps & Pick<RootProps, 'size' | 'labelOn'>
export const IconBox = styled(Box, {
  shouldForwardProp: youShallNotPass('size', 'labelOn'),
})<IconBox>(({ size, labelOn }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '24px',
  height: '24px',
  ...(labelOn &&
    size === 'small' && {
      marginRight: tkn.FabBaseSpacingLabelOnDesktopSmallGap,
    }),
  ...(labelOn &&
    size === 'medium' && {
      marginRight: tkn.FabBaseSpacingLabelOnDesktopMediumGap,
    }),
}))
